import { useEffect, useState } from "react";
import { Pencil, RotateCcw, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const testIdFor = (key) => key.slice(1).replaceAll("/", "-") || "home";

export function SuperPageGuidancePanel() {
  const [pages, setPages] = useState(null);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api
      .get("/super/page-guidance")
      .then(({ data }) => setPages(data.pages))
      .catch((error) => toast.error(errMsg(error)));
  };

  useEffect(() => {
    load();
  }, []);

  const openEditor = (page) => {
    setDraft({ ...page });
    setEditing(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/super/page-guidance", {
        page_key: draft.page_key,
        subtitle: draft.subtitle,
        instructions: draft.instructions,
      });
      toast.success(`${draft.label} guidance published`);
      setEditing(false);
      load();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="mb-8 border-b pb-8"
      data-testid="super-page-guidance-panel"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
            Service settings
          </p>
          <h2 className="mt-1 font-display text-2xl font-bold">Page subtitles & instructions</h2>
          <p
            className="mt-1 text-sm text-muted-foreground"
            data-testid="super-page-guidance-status"
          >
            {pages ? "Each signed-in page has its own help popup." : "Loading page settings…"}
          </p>
        </div>
      </div>
      {pages && (
        <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {pages.map((page) => (
            <div
              key={page.page_key}
              className="flex items-center justify-between gap-3 border p-3"
              data-testid={`page-guidance-row-${testIdFor(page.page_key)}`}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{page.label}</p>
                <p className="text-xs text-muted-foreground">
                  {page.customised ? "Custom guidance published" : "Using starter guidance"}
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                title={`Edit ${page.label} guidance`}
                onClick={() => openEditor(page)}
                data-testid={`page-guidance-edit-${testIdFor(page.page_key)}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <Dialog open={editing} onOpenChange={(open) => !open && setEditing(false)}>
        <DialogContent
          className="max-h-[92vh] max-w-2xl overflow-y-auto"
          data-testid="page-guidance-dialog"
        >
          <DialogHeader>
            <DialogTitle>Edit {draft?.label} guidance</DialogTitle>
            <DialogDescription className="sr-only">
              Update the subtitle and help popup shown for this page.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="space-y-5 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="page-guidance-subtitle">Custom subtitle</Label>
                <Input
                  id="page-guidance-subtitle"
                  value={draft.subtitle}
                  placeholder={draft.default_subtitle}
                  onChange={(event) => setDraft({ ...draft, subtitle: event.target.value })}
                  data-testid="page-guidance-subtitle-input"
                />
                <p className="text-xs text-muted-foreground">
                  Leave blank to keep the page's standard subtitle.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="page-guidance-instructions">Help popup instructions</Label>
                <Textarea
                  id="page-guidance-instructions"
                  className="min-h-48 resize-y"
                  value={draft.instructions}
                  onChange={(event) => setDraft({ ...draft, instructions: event.target.value })}
                  data-testid="page-guidance-instructions-input"
                />
                <p className="text-xs text-muted-foreground">
                  This text appears only in the info popup for {draft.label}.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => setDraft({ ...draft, subtitle: "", instructions: "" })}
              data-testid="page-guidance-restore-defaults-button"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Restore defaults
            </Button>
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => setEditing(false)}
              data-testid="page-guidance-cancel-button"
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button
              disabled={saving}
              onClick={save}
              data-testid="page-guidance-save-button"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Publishing…" : "Publish guidance"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}